import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import { ServerApiClient, type InsightDrillTarget } from '../../../data/ServerApiClient'
import { ensureApiSessionReady, useApiSessionSnapshot } from '../../../data/apiSession'
import {
  claimDemoModule,
  defaultDemoCfoPage,
  lastVisitedDemoModule,
  type DemoGateDecision,
} from '../../../data/demoSession'
import { CfoDemoLeadGate } from '../../cfo-site/CfoDemoLeadGate'
import { CfoUnifiedSurface } from './CfoUnifiedSurface'
import { ReportStudio } from './ReportStudio'
import { AddToReportProvider } from './presentation/useAddToReport'
import {
  buildCfoRoutePath,
  CFO_ROUTE_FOCUS_METRIC_PARAM,
  CFO_ROUTE_FOCUS_TEXT_PARAM,
  CFO_ROUTE_ORIGIN_PARAM,
  cfoMetricFocusFromLocation,
  cfoPageFromRoute,
  cfoRouteFromPage,
  isCfoPageSlug,
  parseCfoRouteState,
  type CfoRouteState,
} from './cfoRoutes'
import type { CfoPageRoute } from './cfoNav'
import { useCfoRuntimeProfile } from './cfoRuntimeProfile'
import { CfoFirmNavigationProvider } from './CfoFirmNavigation'

export type CfoCoreAppProps = {
  workerClient?: AppWorkerClient | null
  topInsetPx?: number
}

type ReportStudioTarget = { workbookId: string; hostWorkbookInstanceId: string }

/** `/?view=report-studio&workbook_id=W&host_workbook_instance_id=H` (opened from the Excel pane). */
const readReportStudioTarget = (loc: Pick<Location, 'search'> | null | undefined): ReportStudioTarget | null => {
  const params = new URLSearchParams(String(loc?.search || ''))
  if (params.get('view') !== 'report-studio') return null
  const workbookId = String(params.get('workbook_id') || '').trim()
  const hostWorkbookInstanceId = String(params.get('host_workbook_instance_id') || '').trim()
  return workbookId && hostWorkbookInstanceId ? { workbookId, hostWorkbookInstanceId } : null
}

const readRouteState = (): CfoRouteState | null => {
  if (typeof window === 'undefined') return null
  return parseCfoRouteState(window.location, { defaultPage: defaultDemoCfoPage() })
}

const replaceRouteIfNeeded = (route: CfoRouteState | null) => {
  if (!route?.needsReplace || typeof window === 'undefined') return
  try {
    const sp = new URLSearchParams(window.location.search || '')
    sp.delete('fg_cfo_section')
    sp.delete('fg_cfo_view')
    sp.delete('cfo_section')
    sp.delete('cfo_view')
    sp.delete('cfoSection')
    sp.delete('cfoView')
    const nextSearch = sp.toString()
    const next = `${route.canonicalPath}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (next !== current) {
      window.history.replaceState(window.history.state, '', next)
      window.dispatchEvent(new Event('fg_route_change'))
    }
  } catch {
    // Route canonicalization should not block rendering.
  }
}

const redirectToCfoLogin = (): boolean => {
  if (typeof window === 'undefined') return false
  try {
    const current = `${window.location.pathname || '/'}${window.location.search || ''}${window.location.hash || ''}`
    if (!current || current.startsWith('/login') || current.startsWith('/auth/')) return false
    const next = current.startsWith('/') && !current.startsWith('//') ? current : '/'
    window.history.replaceState(window.history.state, '', `/login?next=${encodeURIComponent(next)}`)
    window.dispatchEvent(new Event('fg_route_change'))
    return true
  } catch {
    return false
  }
}

const isSessionAuthError = (error: any): boolean => {
  const message = String(error?.body || error?.message || error || '')
  const status = Number(error?.status || 0)
  return status === 401 || status === 403 || /auth|session|token|sign in|login|unauthorized/i.test(message)
}

export function CfoCoreApp({ workerClient = null, topInsetPx = 0 }: CfoCoreAppProps) {
  const appApi = useMemo(() => new ServerApiClient(), [])
  const apiSession = useApiSessionSnapshot()
  const runtimeProfile = useCfoRuntimeProfile()
  const [routeState, setRouteState] = useState<CfoRouteState | null>(() => readRouteState())
  const [sessionError, setSessionError] = useState('')
  // Re-read on every render: route changes re-render through setRouteState.
  const reportStudio = typeof window === 'undefined' ? null : readReportStudioTarget(window.location)
  const [accountingBootstrap, setAccountingBootstrap] = useState<any | null>(null)
  const [accountingBootstrapReady, setAccountingBootstrapReady] = useState(false)
  // Demo lead gate: demo visitors get DEMO_FREE_MODULE_LIMIT modules free;
  // the next distinct module shows the contact gate instead. No-op outside a
  // demo session. Re-evaluated on every route change (nav rail, popstate,
  // marketing deep links all funnel through routeState.page). The token check
  // inside claimDemoModule needs the session bootstrapped, hence the token dep.
  const [demoGate, setDemoGate] = useState<DemoGateDecision>({ gated: false, visited: [] })
  useEffect(() => {
    if (!apiSession.token) return
    setDemoGate(claimDemoModule(routeState?.page || ''))
  }, [routeState?.page, apiSession.token])

  useEffect(() => {
    replaceRouteIfNeeded(routeState)
  }, [routeState])

  useEffect(() => {
    let cancelled = false
    ensureApiSessionReady()
      .then((session) => {
        if (cancelled) return
        if (session.token) {
          setSessionError('')
        }
      })
      .catch((error: any) => {
        if (cancelled) return
        if (isSessionAuthError(error) && redirectToCfoLogin()) return
        setSessionError(error?.message || String(error))
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (
      apiSession.token ||
      !apiSession.initialized ||
      apiSession.bootstrapping
    ) return
    redirectToCfoLogin()
  }, [apiSession.bootstrapping, apiSession.initialized, apiSession.token])

  useEffect(() => {
    if (apiSession.token) setSessionError('')
  }, [apiSession.token])

  useEffect(() => {
    let cancelled = false
    const workbookId = routeState?.workbookId
    if (!apiSession.token || !workbookId) {
      setAccountingBootstrap(null)
      setAccountingBootstrapReady(false)
      return
    }
    if (typeof (appApi as any).getAccountingWorkbookBootstrap !== 'function') {
      setAccountingBootstrap(null)
      setAccountingBootstrapReady(true)
      return
    }
    setAccountingBootstrapReady(false)
    appApi.getAccountingWorkbookBootstrap(workbookId)
      .then((value) => { if (!cancelled) setAccountingBootstrap(value) })
      .catch(() => { if (!cancelled) setAccountingBootstrap(null) })
      .finally(() => { if (!cancelled) setAccountingBootstrapReady(true) })
    return () => { cancelled = true }
  }, [apiSession.token, appApi, routeState?.workbookId])

  useEffect(() => {
    if (!routeState?.workbookId || typeof window === 'undefined') return
    try {
      window.localStorage.setItem('fusegrid_workbook_id', routeState.workbookId)
    } catch {
      // Local storage persistence only helps legacy consumers.
    }
  }, [routeState?.workbookId])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const syncFromLocation = () => {
      const next = readRouteState()
      replaceRouteIfNeeded(next)
      setRouteState(next)
    }
    window.addEventListener('popstate', syncFromLocation)
    // Canonical in-app navigation uses pushState + fg_route_change. Browser
    // popstate only covers Back/Forward, so external route owners such as the
    // guided-tour engine could update the address bar while this component
    // kept rendering the previous module. Listen to the same route contract as
    // App and the CEO surface so the URL and rendered CFO page stay atomic.
    window.addEventListener('fg_route_change', syncFromLocation as EventListener)
    return () => {
      window.removeEventListener('popstate', syncFromLocation)
      window.removeEventListener('fg_route_change', syncFromLocation as EventListener)
    }
  }, [])

  const handleRouteChange = useCallback((
    nextRoute: CfoPageRoute,
    historyMode: 'push' | 'replace' = 'push',
  ) => {
    setRouteState((current) => {
      if (!current || typeof window === 'undefined') return current
      const page = cfoPageFromRoute(nextRoute)
      const nextPath = buildCfoRoutePath({
        siteSlug: current.siteSlug,
        workbookId: current.workbookId,
        page,
      })
      const currentParams = new URLSearchParams(window.location.search || '')
      const nextParams = new URLSearchParams()
      const fromFirmCockpit = currentParams.get(CFO_ROUTE_ORIGIN_PARAM) === 'firm'
      if (fromFirmCockpit) nextParams.set(CFO_ROUTE_ORIGIN_PARAM, 'firm')
      if (page === 'ratios') {
        const focusMetricId = String(nextRoute.focusMetricId || '').trim()
        const existingFocus = cfoMetricFocusFromLocation(window.location)
        if (focusMetricId) {
          nextParams.set(CFO_ROUTE_FOCUS_METRIC_PARAM, focusMetricId)
        } else if (fromFirmCockpit && existingFocus.metricId) {
          nextParams.set(CFO_ROUTE_FOCUS_METRIC_PARAM, existingFocus.metricId)
        }
        if (fromFirmCockpit && existingFocus.text) {
          nextParams.set(CFO_ROUTE_FOCUS_TEXT_PARAM, existingFocus.text)
        }
      }
      const nextSearch = nextParams.toString()
      const nextUrl = `${nextPath}${nextSearch ? `?${nextSearch}` : ''}`
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
      if (nextUrl !== currentPath) {
        const writeHistory = historyMode === 'replace'
          ? window.history.replaceState
          : window.history.pushState
        writeHistory.call(window.history, window.history.state, '', nextUrl)
        window.dispatchEvent(new Event('fg_route_change'))
      }
      return {
        ...current,
        page,
        canonicalPath: nextPath,
        needsReplace: false,
      }
    })
  }, [])

  // "Keep exploring" on the demo gate: return to the most recent module the
  // visitor opened inside the free allowance (home when none qualifies).
  const handleDemoGateBack = useCallback(() => {
    const last = lastVisitedDemoModule()
    const target = last && isCfoPageSlug(last) ? last : defaultDemoCfoPage()
    handleRouteChange(cfoRouteFromPage(target))
  }, [handleRouteChange])

  const handleOpenCfoCore = useCallback((target?: InsightDrillTarget | null) => {
    setRouteState((current) => {
      if (!current || typeof window === 'undefined') return current
      if (target) {
        try {
          const wb = String(current.workbookId || '').trim() || 'local'
          window.sessionStorage.setItem(`fg:cfo-core:deeplink:${wb}`, JSON.stringify({ target, ts: Date.now() }))
        } catch {
          // Deeplink handoff is best-effort; route switching should still work.
        }
      }
      const siteSlug = encodeURIComponent(current.siteSlug)
      const workbookId = encodeURIComponent(current.workbookId)
      const nextPath = `/${siteSlug}/workbooks/${workbookId}/cfo-core`
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
      if (nextPath !== currentPath) {
        window.history.pushState(window.history.state, '', nextPath)
        window.dispatchEvent(new Event('fg_route_change'))
      }
      return current
    })
  }, [])

  if (sessionError) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24, paddingTop: topInsetPx + 24 }}>
        <div style={{ maxWidth: 560 }}>
          <h1 style={{ margin: 0, fontSize: 24 }}>CFO Core session failed</h1>
          <p style={{ color: '#4b5563' }}>{sessionError}</p>
        </div>
      </div>
    )
  }

  if (!apiSession.token) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', paddingTop: topInsetPx }}>
        Loading CFO Core session...
      </div>
    )
  }

  if (reportStudio) {
    return (
      <div style={{ height: '100vh', minHeight: 0, paddingTop: topInsetPx, boxSizing: 'border-box' }}>
        <ReportStudio
          appApi={appApi}
          workbookId={reportStudio.workbookId}
          hostWorkbookInstanceId={reportStudio.hostWorkbookInstanceId}
        />
      </div>
    )
  }

  if (!routeState) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', paddingTop: topInsetPx }}>
        CFO Core route not found.
      </div>
    )
  }

  if (!accountingBootstrapReady) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', paddingTop: topInsetPx }}>
        Loading accounting company…
      </div>
    )
  }

  return (
    <div style={{ height: '100vh', minHeight: 0, paddingTop: topInsetPx, boxSizing: 'border-box' }}>
      <CfoFirmNavigationProvider
        api={appApi}
        siteSlug={routeState.siteSlug}
        currentWorkbookId={routeState.workbookId}
      >
        <AddToReportProvider
          appApi={appApi}
          workbookId={routeState.workbookId}
          onViewReport={() => handleRouteChange({ pageId: 'presentations', view: null })}
        >
          <CfoUnifiedSurface
            appApi={appApi}
            workerClient={workerClient}
            workbookId={routeState.workbookId}
            apiSessionReady={Boolean(apiSession.token)}
            route={{
              ...cfoRouteFromPage(routeState.page),
              ...(routeState.page === 'ratios'
                ? { focusMetricId: cfoMetricFocusFromLocation(window.location).metricId || null }
                : {}),
            }}
            onRouteChange={handleRouteChange}
            onOpenCfoCore={handleOpenCfoCore}
            navProfile="lightweight"
            classicEscapes={false}
            runtimeProfile={runtimeProfile}
            accountingBootstrap={accountingBootstrap}
          />
        </AddToReportProvider>
      </CfoFirmNavigationProvider>
      {demoGate.gated ? (
        <CfoDemoLeadGate
          api={appApi}
          gatedPage={routeState.page}
          visitedPages={demoGate.visited}
          onUnlocked={() => setDemoGate((gate) => ({ ...gate, gated: false }))}
          onBack={handleDemoGateBack}
        />
      ) : null}
    </div>
  )
}
